import { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Badge, Button } from '../atoms';
import {
  StatCard, StatCardRow,
  Tabs,
  DataTable,
  Pagination,
  Modal,
  ActivityPanel,
  Form,
  SchemaField,
  useToast,
} from '../organisms';

/**
 * Organisms demo at /v2/__/organisms.
 * Visual catalog of every Tier-A organism × representative state.
 * Phase 11 Block 6 PR 3a-c (2026-05-12).
 */

interface ParcelRow {
  account_number: string;
  property_name: string;
  type: string;
  proposed: number;
  status: 'in_review' | 'filed' | 'settled' | 'not_filed';
}

const fakeRows: ParcelRow[] = [
  { account_number: '1005301', property_name: '758 Kirby Dr Retail', type: 'Real estate', proposed: 41487022, status: 'in_review' },
  { account_number: '1005300', property_name: '758 Kirby Dr Retail', type: 'Real estate', proposed: 42184686, status: 'filed' },
  { account_number: '1005900', property_name: '2351 Bellaire Blvd Self Storage', type: 'Real estate', proposed: 41297676, status: 'in_review' },
  { account_number: '1005901', property_name: '2351 Bellaire Blvd Self Storage', type: 'Personal property', proposed: 4500000, status: 'settled' },
  { account_number: '1006100', property_name: '7051 Main St Apartment', type: 'Apartment', proposed: 40686712, status: 'settled' },
];

const columns: ColumnDef<ParcelRow>[] = [
  { accessorKey: 'account_number', header: 'Account #' },
  { accessorKey: 'property_name', header: 'Property' },
  { accessorKey: 'type', header: 'Type' },
  {
    accessorKey: 'proposed',
    header: 'Proposed',
    cell: ({ getValue }) => `$${(getValue() as number).toLocaleString()}`,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const s = getValue() as ParcelRow['status'];
      const map: Record<typeof s, { variant: 'success' | 'warning' | 'info' | 'neutral'; label: string }> = {
        in_review: { variant: 'warning', label: 'In Review' },
        filed: { variant: 'success', label: 'Filed' },
        settled: { variant: 'info', label: 'Settled' },
        not_filed: { variant: 'neutral', label: 'Not Filed' },
      };
      const { variant, label } = map[s];
      return <Badge variant={variant} dot>{label}</Badge>;
    },
  },
];

const sampleFormFields: SchemaField[] = [
  { name: 'name', type: 'text', required: true, defaultValue: null, pk: false, enum: null, fk: null,
    label: 'Property name', section: 'Identity', displayOrder: 10,
    immutable: false, hidden: false, conditional: null, enumOptions: null, dataType: 'text' },
  { name: 'property_type', type: 'text', required: true, defaultValue: 'commercial', pk: false, enum: ['commercial', 'residential', 'industrial'], fk: null,
    label: 'Type', section: 'Identity', displayOrder: 20,
    immutable: false, hidden: false, conditional: null,
    enumOptions: [
      { value: 'commercial', label: 'Commercial' },
      { value: 'residential', label: 'Residential' },
      { value: 'industrial', label: 'Industrial' },
    ], dataType: 'text' },
  { name: 'gba', type: 'numeric', required: false, defaultValue: null, pk: false, enum: null, fk: null,
    label: 'Gross building area (sqft)', section: 'Dimensions', displayOrder: 30,
    immutable: false, hidden: false, conditional: null, enumOptions: null, dataType: 'number' },
  { name: 'units', type: 'integer', required: false, defaultValue: null, pk: false, enum: null, fk: null,
    label: 'Units', section: 'Dimensions', displayOrder: 40,
    immutable: false, hidden: false, conditional: { showWhen: { property_type: 'residential' } },
    enumOptions: null, dataType: 'integer' },
  { name: 'tax_exempt', type: 'boolean', required: false, defaultValue: 'false', pk: false, enum: null, fk: null,
    label: 'Tax exempt', section: 'Status', displayOrder: 50,
    immutable: false, hidden: false, conditional: null, enumOptions: null, dataType: 'boolean' },
  { name: 'effective_date', type: 'date', required: false, defaultValue: null, pk: false, enum: null, fk: null,
    label: 'Effective date', section: 'Status', displayOrder: 60,
    immutable: false, hidden: false, conditional: null, enumOptions: null, dataType: 'date' },
];

export function OrganismsDemo() {
  const [activeTab, setActiveTab] = useState('parcels');
  const [modalOpen, setModalOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const { toast } = useToast();

  return (
    <div style={{
      maxWidth: 1200,
      margin: 'var(--sp-10) auto',
      padding: '0 var(--sp-5)',
    }}>
      <header style={{
        borderBottom: '1px solid var(--color-border)',
        paddingBottom: 'var(--sp-5)',
        marginBottom: 'var(--sp-7)',
      }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
        }}>Organisms</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          Phase 11 Block 6 PR 3a-c. StatCard · Tabs · DataTable · Pagination · Modal · ActivityPanel.
        </p>
      </header>

      <Section title="StatCardRow — KPI cards (matches Christophe's entity-detail screens)">
        <StatCardRow>
          <StatCard label="Total assessed value" value="$440,954,614" caption="across 20 parcels" accent="hunter" />
          <StatCard label="Open appeals" value="3" caption="12 in review · 3 under appeal" accent="amber" />
          <StatCard label="Tax-value savings" value="$71,781,888" caption="2 settled parcels" accent="green" />
          <StatCard label="Avg. reduction" value="100.0%" caption="across settled parcels" accent="info" />
        </StatCardRow>
      </Section>

      <Section title="Tabs — entity detail navigation with count badges">
        <Tabs
          activeValue={activeTab}
          onChange={setActiveTab}
          items={[
            { value: 'parcels', label: 'Parcels', count: 20 },
            { value: 'appeals', label: 'Appeals', count: 5 },
            { value: 'contact', label: 'Contact', count: 0 },
            { value: 'documents', label: 'Documents' },
          ]}
        />
        <div style={{ padding: 'var(--sp-4) 0', color: 'var(--color-ink-3)', fontSize: 'var(--fs-md)' }}>
          Active tab: <strong>{activeTab}</strong>
        </div>
      </Section>

      <Section title="DataTable + Pagination — sortable + dense + status pills">
        <DataTable
          data={fakeRows}
          columns={columns}
          onRowClick={(row) => console.log('clicked', row.account_number)}
        />
        <Pagination
          pageIndex={pageIndex}
          pageCount={3}
          pageSize={5}
          totalRows={15}
          onPageChange={setPageIndex}
          onPageSizeChange={(n) => console.log('page size', n)}
        />
      </Section>

      <Section title="Modal — native <dialog>; sm/lg sizes">
        <Button onClick={() => setModalOpen(true)}>Open modal</Button>
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Confirm action"
          size="sm"
          footer={
            <>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={() => setModalOpen(false)}>Confirm</Button>
            </>
          }
        >
          <p style={{ margin: 0 }}>This is a Modal dialog. Click the backdrop or press Escape to close.</p>
        </Modal>
      </Section>

      <Section title="Form — schema-driven (sections · conditional fields · type dispatch)">
        <div style={{
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-lg)',
          padding: 'var(--sp-5)',
        }}>
          <Form
            fields={sampleFormFields}
            onSubmit={(values) => {
              toast({ variant: 'success', message: `Submitted with ${Object.keys(values).length} fields.` });
              console.log('Form submitted', values);
            }}
            onCancel={() => toast({ variant: 'info', message: 'Cancelled.' })}
          />
        </div>
        <p style={{
          margin: 'var(--sp-2) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-sm)',
        }}>
          Try toggling Type to <strong>residential</strong> — the Units field appears (conditional show/hide).
        </p>
      </Section>

      <Section title="ActivityPanel — right-sticky list (typically rendered in a layout sidebar)">
        <div style={{ maxWidth: 380 }}>
          <ActivityPanel
            events={[
              { id: '1', kind: 'success', title: 'Is active · Update', description: 'system', timestamp: 'Apr 30, 2026 8:27 PM' },
              { id: '2', kind: 'info', title: 'Status · Update', description: 'system', timestamp: 'Apr 30, 2026 8:27 PM' },
              { id: '3', kind: 'success', title: 'Is active · Update', description: 'system', timestamp: 'Apr 30, 2026 8:27 PM' },
              { id: '4', kind: 'info', title: 'Status · Update', description: 'system', timestamp: 'Apr 30, 2026 8:27 PM' },
            ]}
          />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 'var(--sp-7)' }}>
      <h2 style={{
        margin: '0 0 var(--sp-4)',
        fontSize: 'var(--fs-xl)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink)',
      }}>{title}</h2>
      {children}
    </section>
  );
}
